import { STEPS } from '../constants/steps';

/**
 * Manages step state and UI across both sides of the card
 */
export default class StepManager {
    /**
     * Create a new StepManager
     * 
     * @param {HTMLElement} container - Container element
     */
    constructor(container) {
        this.container = container;
        this.currentSide = 'front';
        this.activeStep = null;
        this.completedSteps = {
            'front': new Set(),
            'back': new Set()
        };
        this.stepsData = {
            'front': new Map(),
            'back': new Map()
        };
        // this.imageUploader = new ImageUploadHandler();
        this.stepController = null; // Will be set by Designer
        this.handlersReady = false;
        
        this.bindEvents();
    }
    
    /**
     * Set the step controller
     * 
     * @param {object} controller - StepController instance
     */
    setStepController(controller) {
        this.stepController = controller;
    }
    
    /**
     * Called by StepController when handlers are initialized
     */
    notifyHandlersReady() {
        this.handlersReady = true;
        this.render(); // Re-render with handlers
    }
    
    /**
     * Render the step list
     */
    render() {
        const stepsList = this.container.querySelector('.stc-steps-list');
        if (!stepsList) return;
        
        stepsList.innerHTML = '';

        const steps = STEPS[this.currentSide];
        steps.forEach(step => {

            if(!this.stepController.getHandler(step.id)) return;

            const stepEl = document.createElement('div');
            stepEl.className = 'stc-step';
            if (step.id === this.activeStep) stepEl.classList.add('is-active');
            if (this.completedSteps[this.currentSide].has(step.id)) stepEl.classList.add('is-complete');

            // Check if this step has info content
            const hasInfo = !!step.info;
            
            // Create the info icon HTML if needed
            const infoHtml = hasInfo ? 
                `<span class="stc-step-info-icon" data-step-id="${step.id}">i</span>` : '';

            // Create the header
            const header = document.createElement('button');
            header.className = 'stc-step-header';
            header.dataset.step = step.id;
            header.innerHTML = `
                <div class="stc-step-indicator">
                    ${this.completedSteps[this.currentSide].has(step.id) ? '✓' : ''}
                </div>
                <div class="stc-step-content">
                    <div class="stc-step-title">
                        ${step.title}
                        ${step.required ? '<span class="stc-step-required">*</span>' : ''}
                        ${step.price ? `<span class="stc-price">(+$${step.price})</span>` : ''}
                        ${infoHtml}
                    </div>
                    <small class="stc-step-description">${step.description}</small>
                </div>
            `;
            stepEl.appendChild(header);

            // Create panel container
            const panelContainer = document.createElement('div');
            panelContainer.className = 'stc-step-panel-container';
            stepEl.appendChild(panelContainer);

            // Add to the list
            stepsList.appendChild(stepEl);
            
            // If handlers are ready, ask the controller to create a panel
            if (this.handlersReady && this.stepController && this.stepController.hasHandler(step.id)) {
                this.stepController.createPanelForStep(step.id, panelContainer);
            }
            
            // Emit an event that a step was rendered
            this.emit('stepRendered', { stepId: step.id });
        });
    }
    
    /**
     * Bind event listeners
     */
    bindEvents() {
        // Listen for step header clicks
        this.container.addEventListener('click', (e) => {
            const stepHeader = e.target.closest('.stc-step-header');
            if (stepHeader) {
                const stepId = stepHeader.dataset.step;
                this.setActiveStep(stepId);
            }
        });
    }
    
    /**
     * Set the active step
     * 
     * @param {string} stepId - Step ID
     */
    setActiveStep(stepId) {
        const oldStep = this.container.querySelector('.stc-step.is-active');
        const newStep = this.container.querySelector(`[data-step="${stepId}"]`)?.closest('.stc-step');

        if (oldStep) oldStep.classList.remove('is-active');
        if (newStep && oldStep !== newStep) newStep.classList.add('is-active');

        this.activeStep = stepId;
        this.emit('stepChange', { stepId });
    }
    
    /**
     * Change the current side
     * 
     * @param {string} side - 'front' or 'back'
     */
    changeSide(side) {
        if (side !== this.currentSide) {
            this.currentSide = side;
            this.activeStep = null;
            
            // Render with the new side's data
            this.render();
            
            // Emit side change event
            this.emit('sideChange', { side });
        }
    }
    
    /**
     * Set step data with notification
     * 
     * @param {string} stepId - Step ID
     * @param {object} data - Step data
     * @param {boolean} notifyChanges - Whether to emit events (default: true)
     */
    setStepData(stepId, data, notifyChanges = true) {
        // Get existing data or create new object
        const existingData = this.stepsData[this.currentSide].get(stepId) || {};
        
        console.log(this.stepsData[this.currentSide].get(stepId), this.stepsData[this.currentSide]);
        // Merge new data with existing data
        const mergedData = { ...existingData, ...data };
        this.stepsData[this.currentSide].set(stepId, mergedData);
        
        // Update completion state based on data
        if (mergedData && Object.keys(mergedData).length > 0 && 
            !(mergedData.enabled === false)) {
            this.markStepComplete(stepId);
        } else {
            this.markStepIncomplete(stepId);
        }
        
        if (notifyChanges) {
            this.emit('stepDataChange', { 
                stepId, 
                data: mergedData, 
                side: this.currentSide 
            });
        }
    }

    /**
     * Get all step data as an object (for serialization/export)
     * @returns {object} All step data
     */
    getAllStepData() {
        const result = {};
        
        // Convert Maps to plain objects for each side
        ['front', 'back'].forEach(side => {
            result[side] = {};
            this.stepsData[side].forEach((data, stepId) => {
                result[side][stepId] = data;
            });
        });
        
        return result;
    }
    
    /**
     * Get step data for current side
     * 
     * @param {string} stepId - Step ID
     * @returns {object} Step data
     */
    getStepData(stepId) {
        return this.stepsData[this.currentSide].get(stepId);
    }
    
    /**
     * Get step data for any side
     * 
     * @param {string} side - 'front' or 'back'
     * @param {string} stepId - Step ID
     * @returns {object} Step data
     */
    getStepDataForSide(side, stepId) {
        return this.stepsData[side].get(stepId);
    }
    
    /**
     * Set step data for any side
     * 
     * @param {string} side - 'front' or 'back'
     * @param {string} stepId - Step ID
     * @param {object} data - Step data
     * @param {boolean} notifyChanges - Whether to emit events (default: true)
     */
    setStepDataForSide(side, stepId, data, notifyChanges = true) {
        this.stepsData[side].set(stepId, data);

        this.updateCompletedStatusForStep( stepId );
        
        if (notifyChanges) {
            this.emit('stepDataChange', { stepId, data, side });
        }
    }

    updateCompletedStatusForStep(stepId){

        let handler = this.stepController.getHandler(stepId);
        let isComplete = false;

        if( handler ) isComplete = handler.isComplete();

        if (isComplete) this.markStepComplete(stepId);
        else this.markStepIncomplete(stepId);
        
    }
    
    /**
     * Get all completed steps for both sides
     * 
     * @returns {object} Object with completed steps for each side
     */
    getAllCompletedSteps() {
        return {
            front: Array.from(this.completedSteps.front),
            back: Array.from(this.completedSteps.back)
        };
    }
    
    /**
     * Mark step as complete
     * 
     * @param {string} stepId - Step ID
     */
    markStepComplete(stepId) {
        this.completedSteps[this.currentSide].add(stepId);
        const step = this.container.querySelector(`[data-step="${stepId}"]`)?.closest('.stc-step');
        if (step) step.classList.add('is-complete');
        // this.emit('stepComplete', { stepId, side: this.currentSide });
    }
    
    /**
     * Mark step as incomplete
     * 
     * @param {string} stepId - Step ID
     */
    markStepIncomplete(stepId) {
        this.completedSteps[this.currentSide].delete(stepId);
        const step = this.container.querySelector(`[data-step="${stepId}"]`)?.closest('.stc-step');
        if (step) step.classList.remove('is-complete');
        // this.emit('stepIncomplete', { stepId, side: this.currentSide });
    }
    
    /**
     * Clear step data
     * 
     * @param {string} stepId - Step ID
     */
    clearStep(stepId) {
        this.stepsData[this.currentSide].delete(stepId);
        this.markStepIncomplete(stepId);
        
        // Update the step handler's panel if active
        if (this.stepController && this.stepController.hasHandler(stepId)) {
            const handler = this.stepController.getHandler(stepId);
            if (handler) {
                handler.updatePanel();
            }
        }
        
        this.emit('stepCleared', { stepId, side: this.currentSide });
    }
    
    /**
     * Helper method to find step definition
     * 
     * @param {string} stepId - Step ID
     * @param {string} side - Side to check (defaults to current side)
     * @returns {object} Step definition
     */
    getStepDefinition(stepId, side = null) {
        const targetSide = side || this.currentSide;
        return STEPS[targetSide].find(step => step.id === stepId);
    }
    
    /**
     * Get all steps data for both sides
     * This is useful for price calculations and exports
     * 
     * @returns {object} Object with all steps data for each side
     */
    getAllStepsData() {
        const result = { front: {}, back: {} };
        
        // Process front side
        for (const [stepId, data] of this.stepsData.front.entries()) {
            result.front[stepId] = data;
        }
        
        // Process back side
        for (const [stepId, data] of this.stepsData.back.entries()) {
            result.back[stepId] = data;
        }
        
        return result;
    }
    
    /**
     * Emit event
     * 
     * @param {string} eventName - Event name
     * @param {object} detail - Event detail
     */
    emit(eventName, detail = {}) {
        const event = new CustomEvent(`stc:steps:${eventName}`, { detail });
        this.container.dispatchEvent(event);
    }
    
    /**
     * Listen for event
     * 
     * @param {string} eventName - Event name
     * @param {function} callback - Event callback
     */
    on(eventName, callback) {
        this.container.addEventListener(`stc:steps:${eventName}`, callback);
    }
    
    /**
     * Remove event listener
     * 
     * @param {string} eventName - Event name
     * @param {function} callback - Event callback
     */
    off(eventName, callback) {
        this.container.removeEventListener(`stc:steps:${eventName}`, callback);
    }
}